import { AxiosError } from 'axios';
import { Redirect, Route, useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import useAuthSlice from '../Auth/useAuthSlice';
import ConfirmationDialog from '../Dialogs/confirmationDialog';
import { CustomRouteProps } from './models';
import { RootState } from '../../redux/application';

interface PrivateRouteProps<TState = any, TParams = any> extends CustomRouteProps<TState, TParams> {
  requiredPermissions?: string[];
}

function PrivateRoute<TState = any, TParams = any>({
  children,
  render,
  requiredPermissions,
  ...rest
}: PrivateRouteProps<TState, TParams>) {
  const history = useHistory();
  const redux = useAuthSlice();
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);

  try {
    const { wasLogged, refreshFailed, refreshSuccess } = redux.useState();

    if (!wasLogged) {
      return (
        <Redirect
          to={{
            pathname: '/login',
            state: { showSessionDialog: refreshFailed },
          }}
        />
      );
    }

    // Check specific permissions if required
    if (requiredPermissions && requiredPermissions.length > 0) {
      const userPermissions = userInfo?.permissions || [];
      const hasAllPermissions = requiredPermissions.every((permission) =>
        userPermissions.includes(permission)
      );

      if (!hasAllPermissions) {
        return (
          <div className="container mt-5">
            <div className="alert alert-danger">
              <h4>Acesso Negado</h4>
              <p>Você não tem permissão para acessar esta página.</p>
            </div>
          </div>
        );
      }
    }

    return (
      <Route {...rest}>
        {(routeProps) => (
          <>
            <ConfirmationDialog
              show={refreshSuccess}
              title="Ops.."
              handleClose={() => history.go(0)}
              body={
                <p key="ConfirmationRefreshTokenDialog.body">
                  Sua sessão precisou ser renovada devido à inatividade. Caso estivesse registrando
                  informações, é possível que os dados tenham sido perdidos. Por favor, inicie o
                  processo de cadastro novamente.
                </p>
              }
            />
            {render
              ? render({
                  params: routeProps.match?.params as TParams,
                  state: history.location.state as any,
                  refresh: () => history.go(0),
                  path: history.location.pathname,
                  redirect: (path, extra) => {
                    if (!extra) {
                      return history.push(path);
                    }

                    return history.push({
                      pathname: path,
                      state: extra.state,
                      search: extra.params,
                    });
                  },
                  setParams: (params: TParams) =>
                    history.replace(
                      `${history.location.pathname}?${new URLSearchParams(params ?? {}).toString()}`
                    ),
                  setState: (state: TState) =>
                    history.replace(history.location.pathname, {
                      ...(history.location.state as any),
                      ...state,
                    }),
                })
              : children}
          </>
        )}
      </Route>
    );
  } catch (error) {
    if (error as AxiosError) {
      return <Redirect to={{ pathname: '/login', state: { showSessionDialog: true } }} />;
    }
    if (render) {
      return <Route {...rest} />;
    }
    return <Route {...rest}>{children}</Route>;
  }
}

export default PrivateRoute;
